import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { defer, of } from 'rxjs';
import { catchError, finalize, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { GlobalLoaderService } from '../../core/loading/global-loader.service';
import { HealthService } from '../../shared/services/health/health.service';
import { ApiService } from '../../shared/services/api/api.service';
import * as HealthActions from './health.actions';
import { selectHealthLastLoaded } from './health.selectors';

@Injectable()
export class HealthEffects {
  private actions$ = inject(Actions);
  private healthService = inject(HealthService);
  private apiService = inject(ApiService);
  private store = inject(Store);
  private globalLoader = inject(GlobalLoaderService);

  loadHealth$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(HealthActions.loadHealth),
        withLatestFrom(this.store.select(selectHealthLastLoaded)),
        switchMap(([action, lastLoaded]) => {
          // Cache health status for 30 seconds
          const now = Date.now();
          if (lastLoaded && now - lastLoaded < 30 * 1000) {
            // Cached path: no HTTP — keep global loader in sync (see brokers.effects)
            return defer(() => {
              this.globalLoader.begin();
              return of(
                HealthActions.loadHealthSuccess({ health: { status: 'healthy' } as any }),
              ).pipe(finalize(() => this.globalLoader.end()));
            });
          }

          return this.healthService.getHealth(false).pipe(
            map((health) => HealthActions.loadHealthSuccess({ health })),
            catchError((error) => {
              return of(HealthActions.loadHealthFailure({ error }));
            }),
          );
        }),
      );
    },
    { functional: false },
  );
}
